import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../utils/supabase';

/**
 * Subscribe to real-time changes on a session.
 * Calls the appropriate callback whenever participants, rounds, or votes change.
 * Also supports ephemeral broadcast reactions (not stored in DB).
 *
 * Returns { sendReaction } — call this to broadcast a reaction to all participants.
 */
export function useSessionRealtime(sessionId, { onParticipants, onRounds, onVotes, onReaction, onSession } = {}) {
  const channelRef = useRef(null);

  const sendReaction = useCallback((payload) => {
    channelRef.current?.send({
      type: 'broadcast',
      event: 'reaction',
      payload
    });
  }, []);

  const subscribe = useCallback(() => {
    if (!sessionId) return;

    const channel = supabase.channel(`session:${sessionId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'participants',
        filter: `session_id=eq.${sessionId}`
      }, () => onParticipants?.())
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'rounds',
        filter: `session_id=eq.${sessionId}`
      }, () => onRounds?.())
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'votes'
      }, () => onVotes?.())
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'sessions',
        filter: `id=eq.${sessionId}`
      }, () => onSession?.())
      .on('broadcast', { event: 'reaction' }, ({ payload }) => onReaction?.(payload))
      .subscribe();

    channelRef.current = channel;
  }, [sessionId, onParticipants, onRounds, onVotes, onReaction, onSession]);

  useEffect(() => {
    subscribe();
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [subscribe]);

  return { sendReaction };
}
